import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isStandalone: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  updateAvailable: boolean;
  registration: ServiceWorkerRegistration | null;
}

interface PWAConfig {
  enableNotifications?: boolean;
  enableBackgroundSync?: boolean;
  enablePushNotifications?: boolean;
  cacheStrategy?: 'cache-first' | 'network-first' | 'stale-while-revalidate';
}

export const usePWA = (config: PWAConfig = {}) => {
  const [pwaState, setPwaState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    installPrompt: null,
    updateAvailable: false,
    registration: null
  });

  // Verificar se o app está instalado
  const checkInstallStatus = useCallback(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInApp = (window.navigator as any).standalone === true; // iOS Safari
    const isInstalled = isStandalone || isInApp;
    
    setPwaState(prev => ({
      ...prev,
      isInstalled,
      isStandalone
    }));
  }, []);

  // Verificar conectividade
  const checkOnlineStatus = useCallback(() => {
    setPwaState(prev => ({
      ...prev,
      isOnline: navigator.onLine
    }));
  }, []);

  // Registrar Service Worker
  const registerServiceWorker = useCallback(async () => {
    if ('serviceWorker' in navigator) {
      try {
        // Evitar registro duplicado: usar registro existente se houver
        const existing = await navigator.serviceWorker.getRegistration();
        const registration = existing ?? await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        setPwaState(prev => ({
          ...prev,
          registration
        }));

        // Verificar atualizações
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setPwaState(prev => ({
                  ...prev,
                  updateAvailable: true
                }));
              }
            });
          }
        });

        if (!existing) {
          console.log('✅ Service Worker registrado com sucesso');
        } else {
          console.log('ℹ️ Service Worker já estava registrado');
        }
        return registration;
      } catch (error) {
        console.error('❌ Erro ao registrar Service Worker:', error);
        return null;
      }
    }
    return null;
  }, []);

  // Instalar PWA
  const installPWA = useCallback(async () => {
    if (pwaState.installPrompt) {
      try {
        await pwaState.installPrompt.prompt();
        const choiceResult = await pwaState.installPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('✅ PWA instalado com sucesso');
          setPwaState(prev => ({
            ...prev,
            isInstalled: true,
            installPrompt: null
          }));
          return true;
        } else {
          console.log('❌ Instalação do PWA cancelada');
          return false;
        }
      } catch (error) {
        console.error('❌ Erro ao instalar PWA:', error);
        return false;
      }
    }
    return false;
  }, [pwaState.installPrompt]);

  // Atualizar PWA
  const updatePWA = useCallback(async () => {
    if (pwaState.registration && pwaState.updateAvailable) {
      try {
        await pwaState.registration.update();
        window.location.reload();
        return true;
      } catch (error) {
        console.error('❌ Erro ao atualizar PWA:', error);
        return false;
      }
    }
    return false;
  }, [pwaState.registration, pwaState.updateAvailable]);

  // Solicitar permissão para notificações
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('⚠️ Este navegador não suporta notificações');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('⚠️ Notificações foram negadas');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('❌ Erro ao solicitar permissão de notificação:', error);
      return false;
    }
  }, []);

  // Enviar notificação
  const sendNotification = useCallback(async (title: string, options: NotificationOptions = {}) => {
    if (Notification.permission === 'granted' && pwaState.registration) {
      try {
        await pwaState.registration.showNotification(title, {
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-72x72.png',
          ...options
        });
        return true;
      } catch (error) {
        console.error('❌ Erro ao enviar notificação:', error);
        return false;
      }
    }
    return false;
  }, [pwaState.registration]);

  // Configurar notificações push
  const setupPushNotifications = useCallback(async () => {
    if (!pwaState.registration || !config.enablePushNotifications) {
      return false;
    }

    try {
      const permission = await requestNotificationPermission();
      if (!permission) {
        return false;
      }

      // Aqui você configuraria o VAPID key e o serviço de push
      // Por enquanto, apenas retornamos true
      console.log('✅ Notificações push configuradas');
      return true;
    } catch (error) {
      console.error('❌ Erro ao configurar notificações push:', error);
      return false;
    }
  }, [pwaState.registration, config.enablePushNotifications, requestNotificationPermission]);

  // Sincronizar dados em background
  const syncData = useCallback(async (data: any) => {
    if (!pwaState.registration || !config.enableBackgroundSync) {
      return false;
    }

    try {
      // Registrar sync em background
      await (pwaState.registration as any).sync.register('background-sync');
      
      // Armazenar dados para sincronização
      const pendingData = JSON.parse(localStorage.getItem('pendingSync') || '[]');
      pendingData.push({
        ...data,
        timestamp: Date.now()
      });
      localStorage.setItem('pendingSync', JSON.stringify(pendingData));
      
      console.log('✅ Dados agendados para sincronização');
      return true;
    } catch (error) {
      console.error('❌ Erro ao agendar sincronização:', error);
      return false;
    }
  }, [pwaState.registration, config.enableBackgroundSync]);

  // Cachear recursos
  const cacheResources = useCallback(async (urls: string[]) => {
    if (!pwaState.registration) {
      return false;
    }

    try {
      const cache = await caches.open('tamoaki-resources');
      await cache.addAll(urls);
      console.log('✅ Recursos cacheados com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao cachear recursos:', error);
      return false;
    }
  }, [pwaState.registration]);

  // Limpar cache
  const clearCache = useCallback(async () => {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('✅ Cache limpo com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error);
      return false;
    }
  }, []);

  // Verificar se há atualizações
  const checkForUpdates = useCallback(async () => {
    if (pwaState.registration) {
      try {
        await pwaState.registration.update();
        return true;
      } catch (error) {
        console.error('❌ Erro ao verificar atualizações:', error);
        return false;
      }
    }
    return false;
  }, [pwaState.registration]);

  // Event listeners
  useEffect(() => {
    // Event listener para prompt de instalação
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setPwaState(prev => ({
        ...prev,
        isInstallable: true,
        installPrompt: e as BeforeInstallPromptEvent
      }));
    };

    // Event listener para app instalado
    const handleAppInstalled = () => {
      setPwaState(prev => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
        installPrompt: null
      }));
    };

    // Event listeners para conectividade
    const handleOnline = () => checkOnlineStatus();
    const handleOffline = () => checkOnlineStatus();

    // Adicionar event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Inicializar
    checkInstallStatus();
    checkOnlineStatus();
    registerServiceWorker();

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkInstallStatus, checkOnlineStatus, registerServiceWorker]);

  // Configurar notificações push na inicialização
  useEffect(() => {
    if (config.enablePushNotifications) {
      setupPushNotifications();
    }
  }, [config.enablePushNotifications, setupPushNotifications]);

  return {
    ...pwaState,
    installPWA,
    updatePWA,
    requestNotificationPermission,
    sendNotification,
    setupPushNotifications,
    syncData,
    cacheResources,
    clearCache,
    checkForUpdates
  };
};